import React from 'react';
import { Box, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SaveButton, DiscardButton } from '@components/common';
import ActionButton from '@components/common/ActionButton';
import { Counter } from '@components/Counter';

type ChecklistHeaderProps = {
  itemCount: number;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onAddItem: () => void;
  onSave: () => void;
  onDiscard: () => void;
};

export const ChecklistHeader: React.FC<ChecklistHeaderProps> = ({
  itemCount,
  hasUnsavedChanges,
  isSaving,
  onAddItem,
  onSave,
  onDiscard
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: 'background.default',
        borderBottom: '1px solid',
        borderBottomColor: 'divider'
      }}
    >
      <Box
        sx={{
          padding: { xs: 1.5, sm: 3 },
          maxWidth: 1200,
          margin: '0 auto'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 1 },
              minWidth: 0,
              flex: { xs: 'none', sm: 1 }
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Counter title={t('checklist:title')} count={itemCount} />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-end', sm: 'flex-start' }
            }}
          >
            {hasUnsavedChanges && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'warning.main',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 }
                      },
                      animation: 'pulse 2s infinite'
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('checklist:header.unsavedChanges')}
                  </Typography>
                </Box>

                <DiscardButton
                  onClick={onDiscard}
                  disabled={isSaving}
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 }
                  }}
                >
                  {t('checklist:header.discard')}
                </DiscardButton>

                <SaveButton
                  onClick={onSave}
                  loading={isSaving}
                  disabled={isSaving}
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 1 }
                  }}
                >
                  {isSaving
                    ? t('checklist:header.saving')
                    : t('checklist:header.save')}
                </SaveButton>
              </>
            )}

            <ActionButton
              startIcon={<AddIcon />}
              onClick={onAddItem}
              disabled={isSaving}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 1 }
              }}
            >
              {t('checklist:header.addItem')}
            </ActionButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
